import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ConferenceSelectControl } from '@/module/Nomen/Conference/ConferenceSelectControl';
import { PresentationMembershipSelectControl } from '@/module/Nomen/PresentationMembership/PresentationMembershipSelectControl';

import { AuthorListControl, Content, Form, FormSubmit, TextInputControl } from '@/core/components';

import { PresentationSchema } from './model/PresentationSchema';

interface PresentationFormProps extends FormSubmit<PresentationSchema> {
  title: string;
}

export function PresentationForm(props: PresentationFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<PresentationSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <TextInputControl<PresentationSchema>
              name="title"
              label={t('presentations.form.fields.title')}
              isSubmitting={isSubmitting}
            />
            <ConferenceSelectControl<PresentationSchema>
              name="conference"
              label={t('presentations.form.fields.conference')}
              isSubmitting={isSubmitting}
            />
            <TextInputControl<PresentationSchema>
              name="link"
              label={t('presentations.form.fields.link')}
              isSubmitting={isSubmitting}
            />
            <TextInputControl<PresentationSchema>
              name="volumeName"
              label={t('presentations.form.fields.volumeName')}
              isSubmitting={isSubmitting}
            />
          </Content>
          <Content type="full">
            <AuthorListControl<PresentationSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <PresentationMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
